/**
 * Latin digits in every value, whichever keyboard typed them.
 *
 * A Persian keyboard types «۱۲», and a pasted address or note can carry
 * Arabic-Indic figures. Every text field and text area is rewritten to Latin
 * digits as it is typed or pasted, before any handler of the app sees the
 * change, so what is saved and sent always holds 0 to 9. The screen still
 * shows Persian figures: the digit face draws every Latin digit Persian
 * (fonts.css).
 *
 * Only the digits change, one character for one, so the caret stays where
 * it was. A password is left exactly as typed. Numeric fields also turn the
 * Persian decimal and thousands marks into Latin ones (`Input numeric`).
 */

const NOT_TEXT = new Set([
  'password',
  'checkbox',
  'radio',
  'file',
  'range',
  'color',
  'button',
  'submit',
  'reset',
  'image',
  'hidden',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
])

/** A Persian or an Arabic-Indic digit. */
const NON_LATIN = /[۰-۹٠-٩]/
const EVERY_NON_LATIN = /[۰-۹٠-٩]/g

function latin(d: string): string {
  const code = d.charCodeAt(0)
  return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660)
}

/**
 * The value setter of the element's own prototype. Writing through it, and
 * not through `el.value`, leaves React's record of the old value alone, so
 * React still sees the change and runs the field's onChange with the Latin
 * text.
 */
function nativeSetter(el: HTMLInputElement | HTMLTextAreaElement): ((v: string) => void) | undefined {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  return Object.getOwnPropertyDescriptor(proto, 'value')?.set
}

function onInput(e: Event): void {
  const el = e.target
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return
  if (el instanceof HTMLInputElement && NOT_TEXT.has(el.type)) return
  if ((e as InputEvent).isComposing) return
  const before = el.value
  if (!NON_LATIN.test(before)) return
  const after = before.replace(EVERY_NON_LATIN, latin)
  // An email or a number field has no caret to keep: selectionStart is null.
  const start = el.selectionStart
  const end = el.selectionEnd
  const set = nativeSetter(el)
  if (set) set.call(el, after)
  else el.value = after
  if (start !== null && end !== null) el.setSelectionRange(start, end)
}

/**
 * Starts the rewrite for the whole document. Called once, before the app
 * renders; it listens ahead of React, in the capture phase on the document.
 * Returns the way to stop it.
 */
export function keepLatinDigits(doc: Document = document): () => void {
  doc.addEventListener('input', onInput, true)
  return () => doc.removeEventListener('input', onInput, true)
}
